import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LibraryMember, getLibraryMemberById } from '../../../firebase/libraryFirestoreService';
import { FaPrint, FaArrowLeft, FaIdCard, FaQrcode, FaCalendarAlt, FaPhone, FaMapMarkerAlt, FaVenusMars, FaSignature } from '../../../utils/iconUtils';
import { SiGooglefonts } from 'react-icons/si';

const ViewMemberPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<LibraryMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWatermark, setShowWatermark] = useState(true);

  useEffect(() => {
    if (!id) {
      setError('No member id provided');
      setLoading(false);
      return;
    }
    const fetch = async () => {
      setLoading(true);
      const res = await getLibraryMemberById(id);
      if (res.success && res.data) {
        setMember(res.data);
      } else {
        setError(res.error || 'Member not found');
      }
      setLoading(false);
    };
    fetch();
  }, [id]);

  const handlePrint = () => {
    setShowWatermark(false);
    setTimeout(() => {
      window.print();
      setTimeout(() => setShowWatermark(true), 100);
    }, 100);
  };

  const getStatusColor = (status: string) => {
    switch(status?.toLowerCase()) {
      case 'active': return 'bg-emerald-500';
      case 'inactive': return 'bg-amber-500';
      case 'suspended': return 'bg-rose-500';
      default: return 'bg-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading member details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="bg-rose-100 text-rose-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaIdCard className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error Loading Member</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!member) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          .print-id-card {
            box-shadow: none !important;
            border: 1px solid #e5e7eb !important;
            margin: 0 !important;
            page-break-inside: avoid;
          }
        }
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>

      {/* Watermark background pattern (hidden in print) */}
      {showWatermark && (
        <div className="fixed inset-0 pointer-events-none opacity-5 no-print">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, #3b82f6 0, #3b82f6 1px, transparent 0, transparent 50%)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
      )}

      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 no-print">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-soft hover:shadow-md transition-all hover:bg-gray-50 text-gray-700 mb-4"
            >
              <FaArrowLeft className="h-4 w-4" />
              Back to Members
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Member ID Card</h1>
            <p className="text-gray-600 mt-1">View and print library member identification</p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/admin/library/members/edit/${id}`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 bg-white rounded-xl shadow-soft hover:shadow-md transition-all hover:bg-gray-50 text-gray-700"
            >
              <FaSignature className="h-4 w-4" />
              Edit Member
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl shadow-soft hover:shadow-md transition-all hover:from-primary-700 hover:to-primary-800"
            >
              <FaPrint className="h-4 w-4" />
              Print ID Card
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Digital ID Card Preview */}
          <div className="space-y-6 no-print">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
              <div className="relative">
                {/* ID Card Header */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-800 p-6 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                      <div className="h-14 w-14 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-base">
  AIFCC
</div>


                        <div>
                          <h2 className="text-primary-100 font-bold">ALHAJI INUSAH FOUNDATION</h2>
                          <p className="text-primary-100 text-sm">Community Center</p>
                          <p className="text-primary-200 text-xs mt-1">Library Member Identification Card</p>
                        </div>
                      </div>
                      
                    </div>
                    <div className="text-right">
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                        <span className="text-xs font-semibold">MEMBER ID</span>
                        <div className="text-xs mt-0.5"> {member.memId}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ID Card Body */}
                <div className="p-6">
                  <div className="flex gap-6">
                    {/* Photo */}
                    <div className="relative">
                      <div className="w-32 h-32 rounded-xl overflow-hidden border-4 border-white shadow-lg">
                        {member.photoUrl ? (
                          <img 
                            src={member.photoUrl} 
                            alt={member.fullName} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <div className="text-center">
                              <FaIdCard className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                              <span className="text-xs text-gray-500">No Photo</span>
                            </div>
                          </div>
                        )}
                      </div>
                      </div>

                    {/* Member Details */}
                    <div className="flex-1">
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-gray-900">{member.fullName}</h3>
                      
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <FaCalendarAlt className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Joined</p>
                            <p className="text-sm font-medium">{formatDate(member.registrationDate)}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center">
                            <FaCalendarAlt className="h-4 w-4 text-yellow-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">DOB</p>
                            <p className="text-sm font-medium">{formatDate(member.dob)}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
                            <FaVenusMars className="h-4 w-4 text-pink-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Gender</p>
                            <p className="text-sm font-medium">{member.gender}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                            <FaMapMarkerAlt className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Address</p>
                            <p className="text-sm font-medium truncate max-w-[120px]">{member.address || 'Not provided'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ID Card Footer */}
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <FaQrcode className="h-4 w-4" />
                      <span>Scan QR code for verification</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Issued by: AIFCC Library System
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
          
          </div>

          {/* Print-Friendly ID Cards */}
          <div className="space-y-8">
            {/* Print Preview */}
            

            {/* Print Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 no-print">
              <h4 className="font-semibold text-amber-800 mb-2">Printing Instructions</h4>
              <ul className="text-sm text-amber-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="inline-block w-5 h-5 bg-amber-100 rounded-full text-amber-800 text-xs flex items-center justify-center mt-0.5">1</span>
                  Use A4 or Letter size paper in <strong>landscape</strong> orientation
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-block w-5 h-5 bg-amber-100 rounded-full text-amber-800 text-xs flex items-center justify-center mt-0.5">2</span>
                  Set margins to <strong>"None"</strong> or <strong>"Minimum"</strong> in print settings
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-block w-5 h-5 bg-amber-100 rounded-full text-amber-800 text-xs flex items-center justify-center mt-0.5">3</span>
                  Print in <strong>color</strong> for best results
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-block w-5 h-5 bg-amber-100 rounded-full text-amber-800 text-xs flex items-center justify-center mt-0.5">4</span>
                  Laminate after printing for durability
                </li>
              </ul>
            </div>

            {/* Actual Printable ID Cards (2 per page) */}
            <div className="print-only">
              <div className="flex flex-col md:flex-row gap-8 md:gap-4 p-4">
                {/* First ID Card */}
                <div className="print-id-card w-full md:w-1/2 border border-gray-300 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-primary-800 font-bold text-sm">AIFCC LIBRARY</div>
                    <div className="text-xs text-gray-500">MEMBER ID</div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-20 h-24 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                      {member.photoUrl ? (
                        <img src={member.photoUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-xs text-gray-400">PHOTO</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-base mb-1">{member.fullName}</h4>
                      <div className="space-y-1">
                        <div className="text-xs text-gray-700">
                          <div className="font-semibold">ID: {member.memId}</div>
                          <div>Status: <span className={getStatusColor(member.status) + " text-white px-1 rounded"}>{member.status}</span></div>
                        </div>
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <div>DOB: {formatDate(member.dob)}</div>
                          <div>Gender: {member.gender}</div>
                          <div>Since: {formatDate(member.registrationDate)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-2 border-t border-gray-300">
                    <div className="flex justify-between items-center text-xs">
                      <div className="text-gray-500">AIFCC Academic Library</div>
                      <div className="text-gray-700 font-medium">VALID</div>
                    </div>
                  </div>
                </div>

                {/* Second ID Card (duplicate) */}
                <div className="print-id-card w-full md:w-1/2 border border-gray-300 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-primary-800 font-bold text-sm">AIFCC LIBRARY</div>
                    <div className="text-xs text-gray-500">MEMBER ID</div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-20 h-24 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                      {member.photoUrl ? (
                        <img src={member.photoUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-xs text-gray-400">PHOTO</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-base mb-1">{member.fullName}</h4>
                      <div className="space-y-1">
                        <div className="text-xs text-gray-700">
                          <div className="font-semibold">ID: {member.memId}</div>
                          <div>Status: <span className={getStatusColor(member.status) + " text-white px-1 rounded"}>{member.status}</span></div>
                        </div>
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <div>DOB: {formatDate(member.dob)}</div>
                          <div>Gender: {member.gender}</div>
                          <div>Since: {formatDate(member.registrationDate)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-2 border-t border-gray-300">
                    <div className="flex justify-between items-center text-xs">
                      <div className="text-gray-500">AIFCC Academic Library</div>
                      <div className="text-gray-700 font-medium">VALID</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Print disclaimer */}
              <div className="text-center text-xs text-gray-500 mt-8 print-only">
                <p>Official Library Identification • Property of AIFCC Library</p>
                <p className="mt-1">Please return if found • {formatDate(new Date().toISOString())}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewMemberPage;